import * as React from 'react'

import { Button } from '~/components/button/Button'
import { Divider } from '~/components/divider/Divider'
import { styled } from '~/styled'

import { useDataTable } from './DataTableContext'
import { DataTableMetaData } from './DataTableMetaData'

const StyledContainer = styled('div', {
  base: [
    'flex',
    'p-2',
    'w-full',
    'mb-2',
    'justify-between',
    'items-center',
    'min-h-16',
    'rounded-t-sm'
  ],
  variants: {
    isRowSelected: {
      true: ['bg-primary-100']
    }
  }
})

const BulkActionsDefaultActions = ({
  children
}: {
  children: React.ReactElement<unknown>
}): React.ReactElement<unknown> | null => {
  const { rowSelection } = useDataTable()

  if (Object.keys(rowSelection || {}).length > 0) return null

  return children
}

const BulkActionsSelectedRowActions = ({
  cancelLabel = 'Cancel',
  children
}: {
  cancelLabel?: string
  children: React.ReactNode
}) => {
  const { toggleAllPageRowsSelected, rowSelection } = useDataTable()

  const handleDeselectAllPageRows = () => toggleAllPageRowsSelected(false)

  if (Object.keys(rowSelection || {}).length === 0) return null

  return (
    <>
      {children}
      {React.Children.count(children) > 0 && (
        <Divider orientation="vertical" className="mx-4" />
      )}
      <Button theme="neutral" onClick={handleDeselectAllPageRows}>
        {cancelLabel}
      </Button>
    </>
  )
}

export const DataTableBulkActions = Object.assign(
  ({
    children,
    ...rest
  }: {
    children:
      | React.ReactElement<
          React.ComponentProps<typeof BulkActionsDefaultActions>
        >
      | React.ReactElement<
          React.ComponentProps<typeof BulkActionsSelectedRowActions>
        >
      | [
          React.ReactElement<
            React.ComponentProps<typeof BulkActionsDefaultActions>
          >,
          React.ReactElement<
            React.ComponentProps<typeof BulkActionsSelectedRowActions>
          >
        ]
  }) => {
    const { rowSelection } = useDataTable()

    const isRowSelected = Object.keys(rowSelection || {}).length > 0

    return (
      <StyledContainer isRowSelected={isRowSelected} {...rest}>
        <DataTableMetaData />
        <div className="flex items-center justify-end">{children}</div>
      </StyledContainer>
    )
  },
  {
    DefaultActions: BulkActionsDefaultActions,
    SelectedRowActions: BulkActionsSelectedRowActions
  }
)
